// Auto-register all media resources globally
// Import this ONCE in your app's entry point (index.jsx or App.jsx)

import { 
  registerMicrophoneStream, 
  registerAudioContext, 
  registerMediaStreamSource 
} from './microphonePermissionManager';

let interceptorsInstalled = false;

export const installMediaInterceptors = () => {
  if (interceptorsInstalled) return;

  // IMPORTANT: this runs once at app boot (src/index.jsx), BEFORE React mounts.
  // Headless crawlers such as Prerender.io — and any insecure (non-HTTPS) context —
  // expose no `navigator.mediaDevices` and sometimes no `AudioContext`. Touching them
  // unguarded throws synchronously here, React never renders, and #root stays empty —
  // which is exactly the blank page Prerender was caching for the Home route. Guard
  // every access and wrap the whole thing so interceptor setup can NEVER block boot.
  try {
    // 1. Intercept getUserMedia (only when it actually exists)
    const mediaDevices =
      typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
    if (mediaDevices && typeof mediaDevices.getUserMedia === "function") {
      const originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);
      mediaDevices.getUserMedia = function (...args) {
        return originalGetUserMedia(...args).then((stream) => {
          registerMicrophoneStream(stream);
          return stream;
        });
      };
    }

    // 2. Intercept AudioContext (only when the platform provides one)
    const OriginalAudioContext =
      (typeof window !== "undefined" &&
        (window.AudioContext || window.webkitAudioContext)) ||
      null;
    if (OriginalAudioContext) {
      const AudioContextProxy = new Proxy(OriginalAudioContext, {
        construct(target, args) {
          const instance = new target(...args);
          registerAudioContext(instance);

          // Intercept createMediaStreamSource
          const originalCreateSource =
            instance.createMediaStreamSource.bind(instance);
          instance.createMediaStreamSource = function (stream) {
            const source = originalCreateSource(stream);
            registerMediaStreamSource(source);
            return source;
          };

          return instance;
        },
      });

      window.AudioContext = AudioContextProxy;
      if (window.webkitAudioContext) {
        window.webkitAudioContext = AudioContextProxy;
      }
    }

    interceptorsInstalled = true;

    if (process.env.NODE_ENV === "development") {
      console.log("🎤 Media API interceptors installed");
    }
  } catch (err) {
    // Never let interceptor setup crash app boot (an empty #root is what crawlers snapshot blank).
    // eslint-disable-next-line no-console
    console.warn("Media interceptors not installed:", err);
  }
};
